/**
 * PricingEditPage — Ajustement d'un tarif (pattern supersede-by-publish).
 *
 * Route : /ops/pricing/:id/edit
 *
 * Les tarifs sont historisés et immuables : modifier un tarif signifie publier
 * un nouveau POST (qui clôture automatiquement l'ancien). Cette page
 * pré-remplit un formulaire de création avec les valeurs actuelles du tuple
 * (provider, tier, unitType) en laissant l'utilisateur ajuster le prix et
 * la date effective.
 *
 * Accès : ROLE_PLATFORM_ADMIN.
 */

import { useCreate, useCustom } from '@refinedev/core';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Result,
  Select,
  Spin,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { ArrowLeft, DollarSign, Save } from 'lucide-react';
import { useMemo, useEffect, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  PricingRow,
  PublishPricingPayload,
} from './types';
import { PROVIDER_LABELS, TIER_LABELS, UNIT_TYPE_LABELS } from './types';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

interface FormValues {
  priceCents: number;
  currency: string;
  effectiveFrom: Dayjs;
  description?: string;
}

const PricingEditPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();

  const { data, isLoading, isError } = useCustom<{
    success: boolean;
    data: PricingRow[];
  }>({
    url: '/api/v1/admin/pricing',
    method: 'get',
  });

  const row = useMemo<PricingRow | null>(() => {
    const all = data?.data?.data ?? [];
    return all.find((r) => r.id === id) ?? null;
  }, [data, id]);

  useEffect(() => {
    if (row) {
      form.setFieldsValue({
        priceCents: row.priceCents,
        currency: row.currency,
        effectiveFrom: dayjs(),
        description: row.description ?? '',
      });
    }
  }, [row, form]);

  const { mutateAsync: createPricing, isLoading: creating } = useCreate();

  const handleFinish = async (values: FormValues) => {
    if (!row) return;
    const payload: PublishPricingPayload = {
      provider: row.provider,
      tier: row.tier,
      unitType: row.unitType,
      priceCents: values.priceCents,
      currency: values.currency,
      effectiveFrom: values.effectiveFrom.toISOString(),
      description: values.description?.trim() ? values.description.trim() : null,
    };
    try {
      await createPricing({
        resource: 'admin/pricing',
        values: payload,
        successNotification: {
          message: 'Nouveau tarif publié',
          description: `Tarif remplacé pour ${PROVIDER_LABELS[row.provider]} · ${TIER_LABELS[row.tier]}.`,
          type: 'success',
        },
      });
      navigate('/ops/pricing');
    } catch {
      // notification gérée par Refine
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !row) {
    return (
      <Result
        status="404"
        title="Tarif introuvable"
        subTitle="Le tuple recherché n'existe plus ou a été clôturé."
        extra={
          <Button type="primary" onClick={() => navigate('/ops/pricing')}>
            Retour à la liste
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Ajuster le tarif</h1>
            <p className="mt-1 text-sm text-blue-100">
              Publie un nouveau prix qui clôture automatiquement l'ancien
            </p>
          </div>
        </div>
      </div>

      <div className="fc-card p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-900 dark:text-white">
          Tuple cible (immuable)
        </h2>
        <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Provider
            </p>
            <p className="mt-1 font-medium text-slate-900 dark:text-white">
              {row.providerLabel}
            </p>
            <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              {row.provider}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Tier
            </p>
            <p className="mt-1 font-medium text-slate-900 dark:text-white">
              {row.tierLabel}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Unité
            </p>
            <p className="mt-1 font-medium text-slate-900 dark:text-white">
              {UNIT_TYPE_LABELS[row.unitType]}
            </p>
          </div>
        </div>

        <Alert
          className="mt-4"
          type="info"
          showIcon
          message="Tarification historisée"
          description="Les tarifs sont immuables. Le nouveau prix crée une nouvelle ligne ; l'ancienne est automatiquement clôturée à la date effective."
        />
      </div>

      <div className="fc-card p-6">
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            currency: row.currency,
            priceCents: row.priceCents,
            effectiveFrom: dayjs(),
            description: row.description ?? '',
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Form.Item
              label="Nouveau prix (cents)"
              name="priceCents"
              rules={[
                { required: true, message: 'Prix requis' },
                {
                  type: 'number',
                  min: 0,
                  message: 'Prix ne peut pas être négatif',
                },
              ]}
            >
              <InputNumber min={0} step={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Devise"
              name="currency"
              rules={[{ required: true, message: 'Devise requise' }]}
            >
              <Select options={CURRENCY_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Effectif à partir de"
              name="effectiveFrom"
              rules={[{ required: true, message: 'Date requise' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
            <Form.Item
              label="Description (optionnel)"
              name="description"
              className="md:col-span-2"
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Raison du changement, réf. source tarif officiel…"
              />
            </Form.Item>
          </div>

          <div className="flex items-center justify-between">
            <Button
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/ops/pricing')}
            >
              Retour
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={creating}
              icon={<Save className="h-4 w-4" />}
            >
              Publier le nouveau tarif
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default PricingEditPage;
